import {
  Component,
  ChangeDetectionStrategy,
  inject,
  computed,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { ReactiveFormsModule, FormGroup } from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";

import { TypeaheadComponent } from "src/app/core/typeahead/typeahead.component";
import { MeetdayModalFacade } from "../meetday-modal.facade";
import { MeetdayTabCompaniesService } from "./meetday-tab-companies.service";

@Component({
  selector: "app-meetday-tab-companies",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    FontAwesomeModule,
    TypeaheadComponent,
  ],
  providers: [MeetdayTabCompaniesService],
  template: `
    <h3>{{ 'Companies' | translate }}</h3>

    <div class="">
      <div class="input-group">
        <div app-typeahead (selectEvent)="onCompanySelect($event)" [selectorName]="'name'" [apiEndpoint]="'company/all'"></div>
        <div class="input-group-append">
          <button class="btn btn-primary ml-auto" (click)="onAddCompany()">
            {{ 'Add' | translate }}
          </button>
        </div>
      </div>
    </div>

    @if (companiesWithLimits().length > 0) {
    <table class="table table-striped w-100 align-middle">
      <thead>
        <tr>
          <th class="align-middle">{{ 'Company name' | translate }}</th>
          <th class="align-middle">{{ 'Round limit' | translate }}</th>
          <th class="text-right align-middle"></th>
        </tr>
      </thead>
      <tbody>
        @for (company of companiesWithLimits(); track company.companyId) {
        <tr [formGroup]="company.formGroup">
          <td class="align-middle">{{ company.name }}</td>
          <td class="align-middle">
            <div class="input-group input-group-sm w-auto">
              <input type="number" min="1" class="form-control" formControlName="limit" (change)="onLimitChange(company.index)" [placeholder]="getCompanyLimitPlaceholder()" />
              <div class="input-group-append">
                <button class="btn btn-outline-secondary text-nowrap" type="button" (click)="onResetLimit(company.index)" [disabled]="!company.formGroup.get('isOverride')?.value">
                  {{ 'Reset' | translate }}
                </button>
              </div>
            </div>
            @if (company.formGroup.get('isOverride')?.value) {
            <small class="text-info d-block">
              <fa-icon [icon]="faInfoCircle" class="mr-1"></fa-icon>
              {{ 'Custom limit set' | translate }}
            </small>
            }
          </td>
          <td class="text-right align-middle">
            <button class="btn btn-danger" type="button" (click)="onDeleteCompany(company.companyId)">
              {{ 'Delete' | translate }}
            </button>
          </td>
        </tr>
        }
      </tbody>
    </table>
    }
  `,
})
export class MeetdayTabCompaniesComponent {
  private translate = inject(TranslateService);

  // Icons
  faInfoCircle = faInfoCircle;

  // Services
  facade = inject(MeetdayModalFacade);
  companyService = inject(MeetdayTabCompaniesService);

  // Computed: Company map for O(1) lookups
  private companyMap = computed(() => {
    const companies = this.facade.entity().registeredCompanies ?? [];
    return new Map(companies.map((c) => [c.id, c]));
  });

  // Computed: Companies with their form groups
  companiesWithLimits = computed(() => {
    const companyMap = this.companyMap();
    return this.facade.companyLimitsFormArray.controls
      .map((control, index) => {
        const companyId = control.get("companyId")?.value;
        const company = companyMap.get(companyId);
        return {
          index,
          companyId,
          name: company?.name || "",
          formGroup: control as FormGroup,
        };
      })
      .filter((item) => item.name); // Only show companies that exist
  });

  getCompanyLimitPlaceholder(): string {
    const globalLimit = this.facade.globalRoundLimit();
    if (this.facade.isGlobalRoundLimitValid() && globalLimit !== null) {
      return `${globalLimit} (${this.translate.instant("default")})`;
    }
    return this.translate.instant("Unlimited");
  }

  onCompanySelect(event: any): void {
    this.companyService.setSelectedCompany(event.data);
  }

  onAddCompany(): void {
    const meetDayId = this.facade.entity().id;
    if (!meetDayId) {
      return;
    }

    this.companyService.addCompanyToMeetday(meetDayId).subscribe((success) => {
      if (success) {
        this.facade.reloadData();
      }
    });
  }

  onDeleteCompany(companyId: string): void {
    const meetDayId = this.facade.entity().id;
    if (!meetDayId) {
      return;
    }

    this.companyService
      .deleteCompanyFromMeetday(meetDayId, companyId)
      .subscribe((success) => {
        if (success) {
          this.facade.removeCompanyLimit(companyId);
          this.facade.reloadData();
        }
      });
  }

  onLimitChange(index: number): void {
    const meetDayId = this.facade.entity().id;
    if (!meetDayId) {
      return;
    }

    const limitGroup = this.facade.companyLimitsFormArray.at(index);
    const companyId = limitGroup?.get("companyId")?.value;
    const limit = limitGroup?.get("limit")?.value;

    if (!companyId) {
      return;
    }

    // Mark as override when limit is set
    if (limit !== null && !limitGroup?.get("isOverride")?.value) {
      limitGroup?.patchValue({ isOverride: true });
    }

    this.companyService.updateCompanyLimitViaAPI(meetDayId, companyId, limit);
  }

  onResetLimit(index: number): void {
    const limitGroup = this.facade.companyLimitsFormArray.at(index);
    limitGroup?.patchValue({
      limit: null,
      isOverride: false,
    });

    const meetDayId = this.facade.entity().id;
    const companyId = limitGroup?.get("companyId")?.value;

    if (meetDayId && companyId) {
      this.companyService.updateCompanyLimitViaAPI(meetDayId, companyId, null);
    }
  }
}
